import React from 'react';
import gql from 'graphql-tag';
import { Loader } from 'neon-design-system';
import AdminList, { NameCellRenderer, ArticleCountCellRenderer } from '../../ui/admin/AdminList';
import { useQuery } from '@apollo/react-hooks';
import { PromotionalContent } from '../../../shared/types';
import { createStyles, makeStyles } from '@material-ui/core';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import { handleEntityPluralCase } from '../../../utils/string.utils';
import { useArticleFilter } from '../../providers/admin/ArticleFilterProvider';
import { sortArray } from '../../../utils/sorting.utils';
import { formatDisplayDate } from '../../../utils/date.utils';
import { topicsOrCategories } from '../../../utils/utils';
import { TextOverlapping } from '../../ui/TextOverlapping';

const name = 'promotional-content';
const title = 'Promotional Content';

const useStyles = makeStyles(() => createStyles({
	gridHeader: {
		fontWeight: 'bold'
	}
}));

const PROMOTIONAL_CONTENT = gql`
	query promotionalContent{
		promotionalContent {
			_id
			name
			description
			categoriesCount
			articlesCount
			version
			expiryDate
		}
	}
`

const PromotionalContentList = (): JSX.Element => {
	const classes = useStyles();
	const articleFilter = useArticleFilter();
	const columnDefs: ColDef[] = [
		{
			colId: 'promotional-content-name-col',
			headerName: 'Name',
			headerClass: classes.gridHeader,
			field: 'name',
			cellRendererFramework: (props: any) => <NameCellRenderer {...props} apiName={name} />,
			width: 260,
			menuTabs: []
		},
		{
			colId: 'promotional-content-description-col',
			headerName: 'Description',
			headerClass: classes.gridHeader,
			field: 'description',
			cellRendererFramework: (props: ICellRendererParams) => <TextOverlapping value={props.value}	maxLength={130} maxWidth={500} />,
			width: 604,
			menuTabs: []
		},
		{
			colId: 'promotional-content-number-of-categories-col',
			headerName: topicsOrCategories(true, true),
			headerClass: classes.gridHeader,
			field: 'categoriesCount',
			cellRendererFramework: (props: any) => props.value !== 0 ? `${props.value} ${handleEntityPluralCase(props.value, topicsOrCategories(), topicsOrCategories(true))}` : `No ${topicsOrCategories(true)}`,
			width: 160,
			menuTabs: []
		},
		{
			colId: 'promotional-content-number-of-articles-col',
			headerName: 'Articles',
			headerClass: classes.gridHeader,
			field: 'articlesCount',
			cellRendererFramework: (props: ICellRendererParams) => <ArticleCountCellRenderer value={props.value} filter={articleFilter} context={props.context} filterData={{_id: props.data._id, name: props.data.name, type: 'promotional-content'}} />,
			width: 160,
			menuTabs: []
		},
		{
			colId: 'promotional-content-expiry-date-col',
			headerName: 'Expiry Date',
			headerClass: classes.gridHeader,
			field: 'expiryDate',
			cellRendererFramework: (props: any) => props.value ? formatDisplayDate(props.value, false) : '',
			width: 160,
			menuTabs: []
		}
	];

	const promotionalContentQuery = useQuery(PROMOTIONAL_CONTENT, { fetchPolicy: 'network-only' });

	const promotionalContent = (promotionalContentQuery?.data?.promotionalContent as PromotionalContent[]) ?? [];
	const sortedPromotionalContent = sortArray(promotionalContent, 'name', true);

	if (promotionalContentQuery.loading) return <Loader />;

	return (
		<AdminList
			title={title}
			entityForCaptionText={handleEntityPluralCase(promotionalContent.length, 'item')}
			name={name}
			items={sortedPromotionalContent}
			loading={promotionalContentQuery.loading}
			error={promotionalContentQuery.error}
			columns={columnDefs}
			showAdd={true}
		/>
	);
};

export default PromotionalContentList;
